import { Injectable } from '@angular/core';
import { ApplicationInsightsService, RequestTelemetry } from './application-insights.service';
import { TelemetrySnapshot } from './telemetry-context.service';
import { LoggingSettingsService } from './logging-settings.service';
import { LogLevel } from 'src/app/enums/log/log-level.enum';
import { SerializedError } from 'src/app/models/log/serialized-error';

export interface LogEventOptions {
  readonly snapshot?: TelemetrySnapshot;
  readonly operation?: Pick<RequestTelemetry, 'traceId' | 'parentId'>;
}

@Injectable({ 
  providedIn: 'root' 
})
export class LogService {
  private static readonly EXCLUDED_ERROR_PROPS = new Set(['name', 'message', 'stack']);
  
  constructor(
    private applicationInsights: ApplicationInsightsService,
    private loggingSettings: LoggingSettingsService
  ) { }

  /**
   * Record a business or domain level event (non-error) that you want to appear in telemetry dashboards.
   * Use this for significant user or system actions (e.g. user login, data export, workflow step completion)
   * where you want to trend counts or correlate with performance / failures later. Does NOT imply an error.
   *
   * Emits at INFO level to the console (if within current log level) and sends a custom event to Application Insights.
   * Prefer this over info()/debug() when the action itself is meaningful independent of current troubleshooting.
   *
   * @param key  Stable, PascalCase or dotted identifier describing the event (e.g. 'User.Login', 'TimeCard.Submitted').
   * @param data Optional additional structured payload (serializable object). Keep it small & anonymized (no PII).
   *
   * @example
   *  logService.event('User.Login', { method: 'SSO' });
   *  logService.event('Report.Generated', { reportType: 'WeeklySummary', rows: 128 });
   */
  event(key: string, data?: any, options?: LogEventOptions): void {
    try {
      if (LogLevel.INFO <= this.loggingSettings.level) { this.writeToConsole(LogLevel.INFO, key, data); }
    } catch {}
    try { this.applicationInsights.logEvent(key, data, options?.snapshot, options?.operation); } catch {}
  }

  /**
   * Log an application error / exception. Use for unexpected failures or explicit business rule violations that
   * prevented completion of an operation. Automatically sends an exception telemetry item.
   *
   * If you have caught an error and are re-throwing after logging, avoid double logging higher in the stack.
   * Pass a string only when you do not have a real Error instance (it will be wrapped). Attach lightweight context
   * via the data parameter (avoid large objects or circular references).
   *
   * @param key   Identifier (e.g. 'TimeRecord.SaveFailed').
   * @param error The original Error object (preferred) or a string message.
   * @param data  Optional structured context (ids, flags, counts) – exclude PII / large blobs.
   *
   * Examples:
   *  try { await api.save(record); } catch (e) { logService.error('TimeRecord.SaveFailed', e, { id: record.id }); }
   *  if (!user) { logService.error('User.NotFound', 'No active user in session'); }
   */
  error(key: string, error: Error | string, data?: any) {
    const err = this.ensureError(error);
    this.writeToConsole(LogLevel.ERROR, key, { error: err, data });
    this.applicationInsights.logException(err, { name: key, ...data }, 3);
  }

  /**
   * Log a recoverable or noteworthy anomaly. Use when something unexpected happened but the flow can continue
   * (deprecated API fallback, auto-corrected data, soft validation miss, transient network retry, etc.).
   *
   * Avoid using warn for normal control flow or for errors that truly break functionality (use error() there).
   *
   * @param key   Identifier (e.g. 'Sync.RetryScheduled').
   * @param data  Optional structured context.
   * @param alwaysLog Force output even when current LogLevel would suppress WARN.
   *
   * Examples:
   *  logService.warn('Sync.RetryScheduled', 'Will retry in 30s', { attempt });
   *  logService.warn('Permissions.MissingClaim', `Claim ${claim} not present`);
   */
  warn(key: string, data?: any, alwaysLog = false) {
    this.trace(LogLevel.WARN, key, data, alwaysLog);
  }

  /**
   * Log a high-level informational message about application flow or state changes that are generally useful
   * in production when diagnosing user issues (startup milestones, major mode switches, data batch completion).
   * Use sparingly to avoid noise; prefer debug() for chatty / internal details.
   *
   * @param key  Identifier (e.g. 'Sync.Started', 'ReviewPeriod.Loaded').
   * @param data Optional structured context (small & non-sensitive).
   * @param alwaysLog Force output even when current LogLevel would suppress INFO.
   *
   * Examples:
   *  logService.info('Sync.Started');
   *  logService.info('ReviewPeriod.Loaded', { start: rp.startDate, end: rp.endDate });
   */
  info(key: string, data?: any, alwaysLog = false) {
    this.trace(LogLevel.INFO, key, data, alwaysLog);
  }

  /**
   * Log detailed diagnostic information intended mainly for local development or deep troubleshooting.
   * This should be safe to drop in production if log level is higher. Avoid sensitive data. Keep payloads concise.
   *
   * @param key  Identifier (e.g. 'Activity.GroupingResult').
   * @param data Optional verbose context (counts, timings, flags).
   *
   * Examples:
   *  logService.debug('Activity.GroupingResult', { groups: groups.length });
   *  logService.debug('API.Response', { status: res.status });
   */
  debug(key: string, data?: any) {
    this.trace(LogLevel.DEBUG, key, data);
  }

  /**
   * Internal helper used by warn/info/debug to apply level filtering, console output formatting, and telemetry trace.
   * Prefer calling the semantic methods above unless you are extending logging behavior.
   */
  private trace(level: LogLevel, key: string, data?: any, alwaysLog = false) {
    if (level > this.loggingSettings.level && !alwaysLog) return; // log of trace is more verbose than current loggingSettings, skip

    this.writeToConsole(level, key, data);
    this.applicationInsights.logTrace(key, { ...data });
  }

  /**
   * Central console writer honoring the configured log level. Chooses console method based on severity and supports
   * forced logging via alwaysLog. Avoid direct usage from feature code; call the semantic methods instead.
   */
  private writeToConsole(level: LogLevel, key: string, data?: any) {
    const tag = LogLevel[level];
    const serialized = this.safeStringify(data);
    if (level === LogLevel.ERROR) {
      console.error(`[${tag}] ${key}`, serialized);
    } else if (level === LogLevel.WARN) {
      console.warn(`[${tag}] ${key}`, serialized);
    } else {
      console.log(`[${tag}] ${key}`, serialized);
    }
  }

  private ensureError(error: Error | string): Error {
    if (error instanceof Error) {
      return error;
    }
    return new Error(error);
  }

  /**
   * Safely stringifies arbitrary data for console output, guarding against circular references.
   */
  private safeStringify(value: string | number | boolean | object | Error | null | undefined): string {
    if (value === undefined || value === null) return '';
    try {
      const seen = new WeakSet();
      return JSON.stringify(value, (key, val) => {
        if (val instanceof Error) {
          // Create a plain object with error properties
          const errorObj: SerializedError = {
            name: val.name,
            message: val.message,
            stack: val.stack
          };
          // Add any additional enumerable properties
          for (const prop of Object.getOwnPropertyNames(val)) {
            if (!LogService.EXCLUDED_ERROR_PROPS.has(prop)) {
              try {
                errorObj[prop] = val[prop as keyof Error];
              } catch {
                // Ignore accessor errors
              }
            }
          }
          return errorObj;
        }
        if (typeof val === 'object' && val !== null) {
          if (seen.has(val)) return '[Circular]';
          seen.add(val);
        }
        if (typeof val === 'function') return `[Function ${val.name || 'anonymous'}]`;
        return val;
      }, 2);
    } catch (err) {
      // Fallback: try without formatting first
      try {
        return JSON.stringify(value);
      } catch {
        try {
          return String(value);
        } catch {
          return '[Unserializable]';
        }
      }
    }
  }

}
